import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AppContext';
import { Leaf, DollarSign, Package, ExternalLink, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const BudgetHeroWidget = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;
      try {
        const response = await api.get('/budget-hero/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching budget hero stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-muted rounded"></div>
      </div>
    );
  }

  const hasActivity = stats && stats.purchase_count > 0;

  return (
    <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-6" data-testid="budget-hero-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <Leaf className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-heading text-sm uppercase tracking-wide text-emerald-400">Budget Éco</h3>
            <p className="text-xs text-muted-foreground">Anti-gaspillage</p>
          </div>
        </div>
        <Link 
          to="/budget-hero" 
          className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
          data-testid="budget-hero-link"
        >
          Voir tout
          <TrendingUp className="w-3 h-3" />
        </Link>
      </div>

      {hasActivity ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* KG Saved */}
            <div className="bg-background/50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Package className="w-4 h-4 text-emerald-400" />
              </div>
              <p className="text-2xl font-black text-emerald-400" data-testid="kg-saved">
                {stats.total_kg_saved.toFixed(1)}
              </p>
              <p className="text-xs text-muted-foreground">kg sauvés</p>
            </div>

            {/* Dollars Saved */}
            <div className="bg-background/50 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <DollarSign className="w-4 h-4 text-amber-400" />
              </div>
              <p className="text-2xl font-black text-amber-400" data-testid="dollars-saved">
                {stats.total_dollars_saved.toFixed(0)}$
              </p>
              <p className="text-xs text-muted-foreground">économisés</p>
            </div>
          </div>

          {/* CO2 Impact */}
          <div className="bg-emerald-500/10 rounded-xl p-3 text-center">
            <p className="text-sm text-emerald-300">
              <span className="font-bold">{stats.total_co2_saved.toFixed(1)} kg</span> de CO2 évités
            </p>
            <p className="text-xs text-emerald-400/70 mt-1">
              Équivalent à {Math.round(stats.total_co2_saved / 0.21)} km en voiture
            </p>
          </div>
        </>
      ) : (
        /* Empty State */
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-3">
            Commencez à économiser avec Food Hero!
          </p>
          <a
            href="https://www.foodhero.com/fr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="foodhero-link"
          >
            <ExternalLink className="w-4 h-4" />
            Ouvrir Food Hero
          </a>
        </div>
      )}
    </div>
  );
};

export default BudgetHeroWidget;
